import React, { useState } from "react"
import { Dialog } from "@reach/dialog"
import "@reach/dialog/styles.css"
import { Button } from "../shared/Button"
import { LoadingButton } from "../shared/LoadingButton"
import { TextField, FormControlLabel, Checkbox } from "@mui/material"

interface EventCancelModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (canceled_event_message: string, notify_attendees: boolean) => Promise<void>
  ticketsCount: number
  rsvpsCount: number
  hasBandNadaTickets?: boolean
}

export const EventCancelModal: React.FC<EventCancelModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  ticketsCount,
  rsvpsCount,
  hasBandNadaTickets = false,
}) => {
  const [message, setMessage] = useState("")
  const [notifyAttendees, setNotifyAttendees] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleConfirm = async () => {
    setIsSubmitting(true)
    try {
      await onConfirm(message.trim() || "", notifyAttendees)
      onClose()
    } finally {
      setIsSubmitting(false)
    }
  }

  const ticketText = ticketsCount === 1 ? "1 ticket" : `${ticketsCount} tickets`
  const rsvpText = rsvpsCount === 1 ? "1 RSVP" : `${rsvpsCount} RSVPs`
  const countSummary = [ticketsCount > 0 && ticketText, rsvpsCount > 0 && rsvpText]
    .filter(Boolean)
    .join(" and ")

  return (
    <Dialog
      isOpen={isOpen}
      onDismiss={onClose}
      aria-label="Cancel event"
      className="event-cancel-modal"
    >
      <div className="mb-4">
        <h2 className="text-xl font-bold mb-2">Cancel Event?</h2>
        <p className="text-gray-700 mb-4">
          Are you sure you want to cancel? This event has {countSummary}.
          {hasBandNadaTickets && (
            <> All tickets will be refunded.</>
          )}
        </p>
      </div>

      <TextField
        label="Cancel message (optional)"
        placeholder="e.g. Sorry, we had to cancel due to..."
        multiline
        rows={3}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        fullWidth
        className="mb-4"
        variant="outlined"
        size="small"
      />

      <div className="mb-6">
        <FormControlLabel
          control={
            <Checkbox
              checked={notifyAttendees}
              onChange={(e) => setNotifyAttendees(e.target.checked)}
              color="primary"
            />
          }
          label="Notify RSVPs and ticket buyers"
        />
      </div>

      <div className="flex gap-2 justify-end">
        <Button bg="bg-gray-400" onClick={onClose} disabled={isSubmitting}>
          Keep Event
        </Button>
        <LoadingButton
          onClick={handleConfirm}
          isLoading={isSubmitting}
          loadingText="Canceling..."
          bg="bg-red-500"
        >
          Cancel Event
        </LoadingButton>
      </div>
    </Dialog>
  )
}

export default EventCancelModal
